/**
 * 内部消息工作台：会话 / 跨企业搜人 / 加群 / 好友申请箱 / 聊天。
 *
 * 数据流：useImSession（props.im）→ 左栏 Tabs（ConversationList /
 * DirectorySearch / FriendInbox / JoinGroupPanel）+ 右栏（ChatPanel /
 * Composer / SelfProfileButton / FriendActionsDrawer / CreateGroupModal）。
 *
 * 边界提醒：
 * - 建群、加群、发消息 → OpenIM WASM SDK（经 useImSession / gateway）
 * - Nexora /api/im 只负责 bootstrap + 跨企业目录搜人，不代理建群 REST
 */
import { useMemo, useState } from "react";
import { FriendActionsDrawer } from "./FriendActionsDrawer";
import { CreateGroupModal } from "./CreateGroupModal";
import { JoinGroupPanel } from "./JoinGroupPanel";
import {
  Button,
  ConfigProvider,
  Flex,
  Input,
  Layout,
  Tabs,
  Tag,
  Typography,
  theme,
} from "antd";
type Props = { im: ReturnType<typeof useImSession>}; // 内部消息工作台的 props 是 im hook 的返回值
import { useImSession } from "../hooks/useImSession";
import { ChatPanel } from "./ChatPanel";
import { Composer } from "./Composer";
import { ConversationList } from "./ConversationList";
import { DirectorySearch } from "./DirectorySearch";
import { FriendInbox } from "./FriendInbox";
import { SelfProfileButton } from "./SelfProfileButton";
const { Sider, Content } = Layout;

export function ImWorkspace({ im }: Props) {
  const { token } = theme.useToken();
   
  // 会话列表本地过滤关键字
  const [keyword, setKeyword] = useState("");
  // 单聊「更多」抽屉：对方 OpenIM userID；null 表示关闭
  const [friendMoreId, setFriendMoreId] = useState<string | null>(null);
  // 建群弹窗开关（成员从好友多选，不再手输 userID）
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const online = im.state === "ready";
  const pendingCount = im.friendApps.filter((a) => a.handleResult === 0).length;

  const active = im.conversations.find((c) => c.conversationID === im.activeId);

  const visible = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return im.conversations;
    return im.conversations.filter((c) =>
      (c.showName || "").toLowerCase().includes(q),
    );
  }, [im.conversations, keyword]);

  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 16,
          borderRadiusLG: 30,
          borderRadiusSM: 12,
        },
      }}
    >
      <FriendActionsDrawer
        open={Boolean(friendMoreId)} // 是否打开抽屉
        userID={friendMoreId} // 当前操作的对方 OpenIM userID；空则关抽屉
        onClose={() => setFriendMoreId(null)} // 关闭抽屉
        loadProfile={im.getFriendProfile}
        loadGroups={im.getJoinedGroups} // 拉已加入群（给下拉用）
        setRemark={im.setFriendRemark}
        deleteFriend={im.deleteFriend} // 删好友
        addBlack={im.addBlack} // 拉黑
        inviteToGroup={im.inviteToGroup} // 拉入群聊
        onChanged={() => {
          // hook 里已刷新 conversations；此处可按需再拉一次
          setFriendMoreId(null);
        }}

      />
      <Flex
        style={{
          height: "calc(100vh - 64px)",
          padding: token.padding,
          background: token.colorBgLayout,
          boxSizing: "border-box",
        }}
      >
        <Layout
          style={{
            height: "100%",
            borderRadius: token.borderRadiusLG,
            overflow: "hidden",
            background: token.colorBgContainer,
          }}
        >
          <Sider
            width={340}
            theme="light"
            style={{ background: token.colorBgContainer }}
          >
            <Flex
              vertical
              gap={token.paddingSM}
              style={{ height: "100%", padding: token.paddingSM }}
            >
              <Tabs
                size="small"
                style={{ flex: 1, minHeight: 0 }}
                items={[
                  {
                    key: "chats",
                    label: "会话",
                    children: (
                      <Flex vertical gap={8} style={{ height: "62vh" }}>
                        <Input.Search
                          allowClear
                          placeholder="过滤会话"
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                        />
                        <div style={{ flex: 1, overflow: "auto" }}>
                          <ConversationList
                            items={visible}
                            activeId={im.activeId}
                            onSelect={(id) => void im.openConversation(id)}
                            onMoreFriend={(c) => {
                              if (c.userID) setFriendMoreId(c.userID);
                            }}
                          />
                        </div>
                      </Flex>
                    ),
                  },
                  {
                    key: "search",
                    label: "搜人",
                    children: (
                      <div style={{ maxHeight: "62vh", overflow: "auto" }}>
                        <DirectorySearch
                          disabled={!online}
                          onAddFriend={im.addFriend}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "inbox",
                    label:
                      pendingCount > 0 ? `申请(${pendingCount})` : "申请",
                    children: (
                      <div style={{ maxHeight: "62vh", overflow: "auto" }}>
                        <FriendInbox
                          items={im.friendApps}
                          disabled={!online}
                          onAccept={im.acceptFriend}
                          onRefuse={im.refuseFriend}
                        />
                      </div>
                    ),
                  },
                  {
                    // 加群：SDK searchGroups + joinGroup，不走 Nexora REST
                    key: "join-group",
                    label: "加群",
                    children: (
                      <JoinGroupPanel
                        disabled={!online}
                        searchGroups={im.searchGroups}
                        joinGroup={im.joinGroup}
                      />
                    ),
                  },
                ]}
              />
            </Flex>
          </Sider>

          <Content
            style={{
              display: "flex",
              minWidth: 0,
              background: token.colorBgLayout,
            }}
          >
            <Flex vertical style={{ width: "100%", minHeight: 0 }}>
              <Flex
                align="center"
                gap={token.marginXS}
                style={{
                  padding: `${token.paddingSM}px ${token.padding}px`,
                }}
              >
                <Typography.Text strong>
                  {active?.showName || "内部消息"}
                </Typography.Text>
                <Tag color={online ? "gold" : "default"}>
                  {online ? "在线" : "离线"}
                </Tag>
                <SelfProfileButton 
                  selfId={im.selfId}
                  profile={im.selfProfile}
                  disabled={!online}
                  onSaveNickname={im.updateNickname}
                  onUploadAvatar={im.updateAvatar}
                />
              </Flex>

              {im.error ? (
                <Typography.Text
                  type="danger"
                  style={{ padding: `0 ${token.padding}px` }}
                >
                  {im.error}
                </Typography.Text>
              ) : null}

              {/* 建群入口：打开好友多选弹窗，替代「逗号粘 userID」 */}
              <Flex
                wrap
                gap={token.marginXS}
                style={{
                  padding: `0 ${token.padding}px ${token.paddingXS}px`,
                }}
              >
                <Button
                  type="primary"
                  disabled={!online}
                  onClick={() => setCreateGroupOpen(true)}
                >
                  创建群聊
                </Button>
              </Flex>

              <ChatPanel selfId={im.selfId} messages={im.messages} />
              <Composer
                disabled={!online || !im.activeId}
                onSend={im.sendText}
                onSendImage={im.sendImage}
                onSendVideo={im.sendVideo}
                onSendFile={im.sendFile}
              />
            </Flex>
          </Content>
        </Layout>
      </Flex>

      {/* 建群：只调 im.createGroup → OpenIM SDK，无 /api/im/createGroup */}
      <CreateGroupModal
        open={createGroupOpen}
        disabled={!online}
        onClose={() => setCreateGroupOpen(false)}
        loadFriends={im.listFriends}
        onCreate={async (name, memberIds) => {
          const gid = await im.createGroup(name, memberIds);
          return gid;
        }}
      />
    </ConfigProvider>
  );
}
